import React, { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';

import FetchFailed from 'reusable/FetchFailed';
import { useLatestRequest } from 'reusable/useLatestRequest';
import { CompanySupportAccess, currentUserId, customerDeadline, listCompanySupportAccesses } from './supportApi';
import { useSupportDate } from './supportUi';

/**
 * YAC-2026-009 PR7b。お客様の管理者の画面（ダッシュボード）に、今効いている運営のサポートを出す
 * （GET /v1/company/support_accesses/。1件以上あるときだけ）。「運営のサポート（期限 ○日 ○時まで）」。
 * サポートのセッション自身にも見え、support_user が自分なら「（あなた）」を添える。
 * 期限は実際に効く期限（effective_until。帯の期限と同じ）。記録の期限（expires_at）ではない（2026-10-26）。
 *
 * ダッシュボードに置いた理由: 管理者がログインの後に必ず最初に開く画面で、運営が入れる状態に気付ける。会社の設定の
 * 画面は時間帯の変更だけの画面で、開くことが少ない（気付けない）。
 */
const CustomerSupportNotice: React.FC = () => {
  const { t } = useTranslation('layout');
  const formatDate = useSupportDate();
  const titleId = useId();
  const [rows, setRows] = useState<CompanySupportAccess[]>([]);
  const [error, setError] = useState<unknown>(null);
  const beginRequest = useLatestRequest();

  const load = useCallback(async () => {
    const isCurrent = beginRequest();
    setError(null);
    try {
      const loaded = await listCompanySupportAccesses();
      if (isCurrent()) setRows(loaded);
    } catch (failure) {
      if (!isCurrent()) return;
      console.error('Failed to fetch support accesses', failure);
      setError(failure);
    }
  }, [beginRequest]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) {
    return (
      <div className="rounded-md bg-white">
        <FetchFailed error={error} onRetry={load} message={t('support.customer-failed')} compact />
      </div>
    );
  }
  if (rows.length === 0) return null;
  const me = currentUserId();
  return (
    <section
      aria-labelledby={titleId}
      data-customer-support
      className="flex gap-3 rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900"
    >
      <ShieldExclamationIcon className="h-6 w-6 shrink-0" aria-hidden="true" />
      <div className="flex flex-col gap-1">
        <h2 id={titleId} className="text-subtitle-2 font-bold">
          {t('support.customer-title')}
        </h2>
        <ul className="flex flex-col gap-1 text-body-2">
          {rows.map((row) => (
            <li key={row.id}>
              {t('support.customer-notice', { date: formatDate(customerDeadline(row)) })}
              {row.support_user === me ? t('support.customer-you') : ''}
            </li>
          ))}
        </ul>
        <p className="text-caption">{t('support.customer-lead')}</p>
      </div>
    </section>
  );
};

export default CustomerSupportNotice;
